// The membership editor's sentences and lists, as pure functions.
//
// A pure module rather than logic inside the component, for this repository's
// standing reason: there is no DOM harness, so anything decidable without a DOM is
// decided here and asserted by test/web/queue-members.test.js — the shape
// web/src/pages/admin/descriptor-form.js and on-shift.js established.
//
// The property that matters is the same one on-shift.js states: TOTALITY. A
// capability note with a hole in it renders an empty cell beside a name, and a
// blank cell reads as "fine" — which is precisely the silence this column exists
// to break. `memberCapability` returns a non-empty sentence for every input,
// including a row that arrived without the column at all.

/**
 * What to say beside one member about whether routing will ever pick them.
 *
 * The gates are ordered as `ELIGIBLE_CTE_SQL` evaluates them
 * (src/routing/assign.js): `is_active` before `can_be_assigned`. The order is
 * load-bearing rather than cosmetic — telling a lead "their Entra role cannot own
 * tickets" about a deactivated account sends them to change a group that will
 * change nothing.
 *
 * This screen deliberately knows nothing about shifts or about the queue's own
 * `is_assignable`; the on-shift preview on the same page owns those two gates and
 * reads them from the router's own query. Restating them here would be a fourth
 * copy of the eligibility rules, which is the mistake src/routing/eligibility.js's
 * header records.
 *
 * @param {object} member  One entry of `GET /api/admin/queues/:id/members`.
 * @returns {{assignable: boolean, text: string}}  `text` is always non-empty.
 */
export function memberCapability(member) {
  const row = member ?? {};

  if (row.is_active === false) {
    return {
      assignable: false,
      text: 'Their account is not active, so routing skips them.',
    };
  }
  if (row.can_be_assigned === false) {
    return {
      assignable: false,
      // Names the fix and says where it is NOT. `can_be_assigned` is a persisted
      // computed column from `users.role`, which migration 010's trigger maintains
      // from `user_product_roles`, which only the Entra sync writes — so there is
      // no control on this page, or anywhere in this console, that could restore
      // it, and a note that only said "cannot be assigned" would send a lead
      // looking for one.
      text: 'Cannot be assigned, so routing skips them. Their Entra app role decides this, '
        + 'and it cannot be changed here.',
    };
  }
  if (row.can_be_assigned === true) {
    return { assignable: true, text: 'Can be assigned' };
  }

  // Neither true nor false: an older build, a partial response, a renamed key.
  // Saying so beats a blank cell, which reads as "fine" — the exact failure this
  // column was added to prevent one level up.
  return {
    assignable: false,
    text: 'Capability unknown. This row arrived without can_be_assigned. Reload before '
      + 'trusting this list.',
  };
}

/** The ids of the current membership, in order, with anything unusable dropped. */
export function memberIds(members) {
  return (Array.isArray(members) ? members : [])
    .map((member) => member?.id)
    .filter((id) => id !== null && id !== undefined);
}

/**
 * The "Add someone" options: every candidate who is not already a member.
 *
 * Someone who cannot be assigned is OFFERED, and labelled. Membership and
 * capability are two different facts (design §8.1) and a lead has legitimate
 * reasons to put somebody in a queue before Entra catches up — refusing here
 * would be this console quietly inventing a rule the API does not have.
 *
 * @param {object[]} candidates  `GET /api/users`.
 * @param {object[]} members     The current membership.
 * @returns {{id: string, label: string}[]}
 */
export function candidateOptions(candidates, members) {
  const taken = new Set(memberIds(members));
  return (Array.isArray(candidates) ? candidates : [])
    .filter((c) => c && c.id !== null && c.id !== undefined && !taken.has(c.id))
    .map((c) => {
      // `display_name` explicitly, never the row: joining a list of objects
      // printed "[object Object]" on this console's list screen earlier in this
      // plan, silently, past the build and every test. The fallbacks exist so an
      // option is never blank — an unlabelled <option> is unclickable in practice.
      const name = c.display_name || c.email || `User ${c.id}`;
      return {
        id: c.id,
        label: c.can_be_assigned === false ? `${name} (cannot be assigned)` : String(name),
      };
    });
}
